/**
 * TanStack Query keys for EVERY route of the shared table (mirrors vocdoni-app src/queries/keys.ts).
 * SHARED, FROZEN after Phase 0: organizer pages use api/organizer.ts, member pages use api/member.ts; ask the
 * Phase-0 owner (or coordinate in the handoff) before adding keys here.
 *
 * Prefix rules (invalidate a whole subtree with its prefix):
 * - ['org', …]     organizer session data (cleared on login / logout)
 * - ['public', …]  unauthenticated data (organization landing, voting page, unsubscribe)
 * - ['member', orgSlug, …] the member session of ONE organization (cleared on member logout)
 */
const org = (orgId: string) => ['org', 'organizations', orgId] as const
const election = (orgId: string, electionId: string) => [...org(orgId), 'elections', electionId] as const

export const queryKeys = {
  health: ['health'] as const,
  publicConfig: ['public-config'] as const,
  org: {
    me: ['org', 'me'] as const,
    organizations: ['org', 'organizations'] as const,
    slugAvailable: (slug: string) => ['org', 'slug-available', slug] as const,
    organization: org,
    dashboard: (orgId: string) => [...org(orgId), 'dashboard'] as const,
    map: (orgId: string, filters: Record<string, unknown> = {}) => [...org(orgId), 'map', filters] as const,
    /** Prefix of every member list / detail of the organization. */
    membersRoot: (orgId: string) => [...org(orgId), 'members'] as const,
    members: (orgId: string, filters: Record<string, unknown> = {}) =>
      [...org(orgId), 'members', 'list', filters] as const,
    member: (orgId: string, memberId: number) => [...org(orgId), 'members', memberId] as const,
    liveDecision: (orgId: string, memberId: number, sessionId: string) =>
      [...org(orgId), 'members', memberId, 'live-decision', sessionId] as const,
    reviewQueue: (orgId: string, query: Record<string, unknown> = {}) =>
      [...org(orgId), 'review-queue', query] as const,
    communicationsRoot: (orgId: string) => [...org(orgId), 'communications'] as const,
    communications: (orgId: string, query: Record<string, unknown> = {}) =>
      [...org(orgId), 'communications', 'list', query] as const,
    communication: (orgId: string, communicationId: string) =>
      [...org(orgId), 'communications', communicationId] as const,
    recipients: (orgId: string, communicationId: string, query: Record<string, unknown> = {}) =>
      [...org(orgId), 'communications', communicationId, 'recipients', query] as const,
    audience: (orgId: string, query: Record<string, unknown> = {}) =>
      [...org(orgId), 'communications', 'audience', query] as const,
    elections: (orgId: string) => [...org(orgId), 'elections'] as const,
    election,
    electionDashboard: (orgId: string, electionId: string) => [...election(orgId, electionId), 'dashboard'] as const,
    electionResults: (orgId: string, electionId: string) => [...election(orgId, electionId), 'results'] as const,
    /** Election committees (councils) linked to the organization. */
    councils: (orgId: string) => [...org(orgId), 'councils'] as const,
  },
  public: {
    organization: (orgSlug: string) => ['public', 'orgs', orgSlug] as const,
    election: (orgSlug: string, electionSlug: string) =>
      ['public', 'orgs', orgSlug, 'elections', electionSlug] as const,
    unsubscribe: (token: string) => ['public', 'unsubscribe', token] as const,
  },
  member: {
    /** Prefix of everything of the member session of one organization. */
    root: (orgSlug: string) => ['member', orgSlug] as const,
    registration: (orgSlug: string) => ['member', orgSlug, 'registration'] as const,
    me: (orgSlug: string) => ['member', orgSlug, 'me'] as const,
    elections: (orgSlug: string) => ['member', orgSlug, 'elections'] as const,
    election: (orgSlug: string, electionSlug: string) => ['member', orgSlug, 'elections', electionSlug] as const,
    votes: (orgSlug: string, electionSlug: string) => ['member', orgSlug, 'elections', electionSlug, 'votes'] as const,
  },
}
